/**
 * Shared helpers for parsing/sanitizing rich-text HTML that comes from the
 * question/explanation API responses (question paper, exam review, bookmarks).
 * Keep all question-HTML parsing logic centralized here to avoid drift
 * between the different screens that render this content.
 */

/**
 * Removes embedded <img> tags from question/explanation HTML.
 * The API sometimes embeds a duplicate/broken <img> tag inside the rich text
 * in addition to providing a separate image URL field, so we strip it here
 * to avoid rendering the image twice (once broken, once correctly).
 */
export function stripEmbeddedImages(html: string | null | undefined): string {
  return html ? html.replace(/<img\b[^>]*>/gi, '') : '';
}

/**
 * Points the <img> placeholders inside question/explanation HTML (e.g. an image
 * cell in a table) at the image URLs from the separate API field, in order, so
 * the image renders where the author placed it instead of below the content.
 * Placeholders beyond the available URLs are dropped (their own src is broken),
 * and URLs without a placeholder are returned so they can still be shown below.
 */
export function embedImagesInHtml(
  html: string | null | undefined,
  imageUrls: string[],
): { html: string; remainingImageUrls: string[] } {
  if (!html) {
    return { html: '', remainingImageUrls: imageUrls };
  }

  let usedCount = 0;
  const embeddedHtml = html.replace(/<img\b[^>]*>/gi, (tag) => {
    if (usedCount >= imageUrls.length) {
      // Keep images whose own src is a real URL (e.g. bookmark explanations
      // that inline the image with no separate URL field); drop broken placeholders.
      return hasUsableImageSrc(tag) ? tag : '';
    }
    const src = imageUrls[usedCount++].replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    return tag
      .replace(/\s(?:src|srcset)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/^<img\b/i, `<img src="${src}"`);
  });

  return { html: embeddedHtml, remainingImageUrls: imageUrls.slice(usedCount) };
}

/**
 * Keeps explanation HTML exactly as received (each <img> keeps its own src) and
 * returns the separate image URLs to render below it, skipping any URL the HTML
 * already shows so the same image is not rendered twice.
 */
export function splitExplanationImages(
  html: string | null | undefined,
  imageUrls: string[],
): { html: string; remainingImageUrls: string[] } {
  const embeddedSrcs = new Set((html?.match(/<img\b[^>]*>/gi) ?? []).map(getImageSrc).filter(Boolean));
  return {
    html: html ?? '',
    remainingImageUrls: imageUrls.filter((url) => !embeddedSrcs.has(url)),
  };
}

function hasUsableImageSrc(tag: string): boolean {
  return /^(?:https?:)?\/\/|^data:image\//i.test(getImageSrc(tag));
}

function getImageSrc(tag: string): string {
  const match = tag.match(/\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  return (match?.[1] ?? match?.[2] ?? match?.[3] ?? '').trim().replace(/&amp;/g, '&');
}

/**
 * Strips all HTML tags from a string, collapsing whitespace, so that rich
 * question/explanation HTML can be safely shown as plain-text previews
 * (e.g. list rows, sidebar cards) without leaking raw markup.
 */
export function stripHtmlTags(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalizes an image URL field from the API into a clean list of URLs.
 * The backend may send a single URL, an array of URLs, a JSON-encoded array
 * string or a comma-separated string, so every shape is handled here.
 */
export function normalizeImageUrls(value: string | string[] | null | undefined): string[] {
  if (!value) {
    return [];
  }

  let urls: unknown[];
  if (Array.isArray(value)) {
    urls = value;
  } else {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        urls = Array.isArray(parsed) ? parsed : [trimmed];
      } catch {
        urls = trimmed.slice(1, -1).split(',');
      }
    } else {
      urls = trimmed.split(/,(?=\s*(?:https?:|data:|\/))/);
    }
  }

  return Array.from(
    new Set(
      urls
        .filter((url): url is string => typeof url === 'string')
        .map((url) => url.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean),
    ),
  );
}
